import * as Phaser from 'phaser';
import { Mirror } from '../entities/Mirror';
import { Prism } from '../entities/Prism';
import { PALETTE } from '../render/palette';
import { ensureTouchHitArea } from './responsive-font';

export type PieceKind = 'mirror' | 'prism';

export interface PiecePickerOptions {
  /** Cell-centre x in scene coords; picker anchors above this point. */
  anchorX: number;
  /** Cell-centre y in scene coords. */
  anchorY: number;
  mirrorEnabled: boolean;
  prismEnabled: boolean;
  /** Fires when the user picks one of the enabled devices. */
  onPick: (kind: PieceKind) => void;
  /** Fires when the user clicks outside the card to dismiss. */
  onDismiss: () => void;
}

const CARD_W = 110;
const CARD_H = 56;
const CARD_RADIUS = 8;
const CARD_OFFSET_Y = 50; // distance from slot centre to card centre
const SHIELD_DEPTH = 1500;
const PICKER_DEPTH = 1501;

/**
 * A small popover offered when the player clicks an empty slot. Two buttons —
 * Mirror and Prism — are arranged horizontally; each is enabled per the
 * caller's discretion (prism is gated behind level 6 unlock and slot type).
 *
 * The popover anchors above the slot. A dashed connector tail points down.
 * A full-canvas shield catches clicks outside the card and dismisses.
 */
export class PiecePicker {
  private readonly scene: Phaser.Scene;
  private readonly opts: PiecePickerOptions;
  private readonly shield: Phaser.GameObjects.Rectangle;
  private readonly container: Phaser.GameObjects.Container;
  private isDestroyed = false;

  constructor(scene: Phaser.Scene, opts: PiecePickerOptions) {
    this.scene = scene;
    this.opts = opts;

    this.shield = scene.add
      .rectangle(480, 360, 2000, 2000, 0x06060d, 0.45)
      .setOrigin(0.5)
      .setDepth(SHIELD_DEPTH)
      .setInteractive();
    this.shield.on('pointerdown', () => {
      this.opts.onDismiss();
    });

    // Place card above the slot; flip below if too close to the top.
    const cardCx = opts.anchorX;
    const aboveCy = opts.anchorY - CARD_OFFSET_Y;
    const below = aboveCy - CARD_H / 2 < 12;
    const cardCy = below ? opts.anchorY + CARD_OFFSET_Y : aboveCy;
    const tailFromY = below ? cardCy - CARD_H / 2 : cardCy + CARD_H / 2;
    const tailToY = below ? opts.anchorY - 26 : opts.anchorY + 26;

    this.container = scene.add.container(0, 0).setDepth(PICKER_DEPTH);

    // Dashed connector tail.
    const tail = scene.add.graphics();
    tail.lineStyle(1, PALETTE.HUD_TITLE, 0.4);
    const step = 6;
    const dir = tailToY > tailFromY ? 1 : -1;
    let cursor = tailFromY;
    while (dir > 0 ? cursor < tailToY : cursor > tailToY) {
      const end = dir > 0 ? Math.min(cursor + 3, tailToY) : Math.max(cursor - 3, tailToY);
      tail.lineBetween(cardCx, cursor, cardCx, end);
      cursor += dir * step;
    }
    this.container.add(tail);

    // Card body.
    const body = scene.add.graphics();
    body.fillStyle(0x0e0e18, 0.95);
    body.fillRoundedRect(cardCx - CARD_W / 2, cardCy - CARD_H / 2, CARD_W, CARD_H, CARD_RADIUS);
    body.lineStyle(1, PALETTE.HUD_TITLE, 0.4);
    body.strokeRoundedRect(cardCx - CARD_W / 2, cardCy - CARD_H / 2, CARD_W, CARD_H, CARD_RADIUS);
    // Centre divider.
    body.lineStyle(1, PALETTE.HUD_TITLE, 0.18);
    body.lineBetween(cardCx, cardCy - CARD_H / 2 + 8, cardCx, cardCy + CARD_H / 2 - 8);
    this.container.add(body);

    // Mirror button (left half).
    const mirrorBtnX = cardCx - CARD_W / 4;
    const mirrorBtn = this.makeButton(mirrorBtnX, cardCy, opts.mirrorEnabled, () => {
      opts.onPick('mirror');
    });
    const mirrorIcon = new Mirror({
      scene,
      cellSize: 64,
      gridX: 0,
      gridY: 0,
      initialAngleDeg: -45,
    });
    mirrorIcon.setPosition(mirrorBtnX, cardCy);
    mirrorIcon.setScale(0.62);
    mirrorIcon.setAlpha(opts.mirrorEnabled ? 1 : 0.3);
    this.container.add([mirrorBtn, mirrorIcon]);

    // Prism button (right half).
    const prismBtnX = cardCx + CARD_W / 4;
    const prismBtn = this.makeButton(prismBtnX, cardCy, opts.prismEnabled, () => {
      opts.onPick('prism');
    });
    const prismIcon = new Prism({
      scene,
      cellSize: 64,
      gridX: 0,
      gridY: 0,
    });
    prismIcon.setPosition(prismBtnX, cardCy);
    prismIcon.setScale(0.55);
    prismIcon.setAlpha(opts.prismEnabled ? 1 : 0.3);
    this.container.add([prismBtn, prismIcon]);
  }

  destroy(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;
    this.shield.destroy();
    this.container.destroy(true);
  }

  private makeButton(
    x: number,
    y: number,
    enabled: boolean,
    onClick: () => void,
  ): Phaser.GameObjects.Rectangle {
    const hit = this.scene.add
      .rectangle(x, y, 44, 40, 0xffffff, 0)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: enabled });
    // The button is 44×40; expand height to ≥44 on touch.
    ensureTouchHitArea(hit, this.scene, Phaser.Geom.Rectangle);
    if (enabled) {
      hit.on('pointerover', () => hit.setFillStyle(PALETTE.HUD_TITLE, 0.08));
      hit.on('pointerout', () => hit.setFillStyle(0xffffff, 0));
      hit.on('pointerdown', () => onClick());
    }
    return hit;
  }
}
